export interface Curve {
  id: string;
  name: string;
  description: string;
  start: {
    type: string;
    offset: any;
    duration: string;
    power: any;
  };
  end: {
    type: string;
    offset: any;
    duration: string;
    power: any;
  };
  color: string;
  datasets: number[];
  labels: string[];
  timezone: string;
}

export interface TableHeaders {
  value: string;
  text: string;
  sortable?: boolean;
}

export interface Route {
  createFilter?: boolean;
  cluster?: boolean;
  realPath?: string;
  path?: string;
  name?: string;
  menuTitle?: string;
  role?: string[];
  menuIcon?: string;
  menuImg?: string;
  menuSelectedImg?: string;
  module?: string | false;
  component?: any;
  redirect?: string;
  meta?: {
    title?: string;
    auth?: boolean;
    company?: boolean;
    sidebar?: boolean;
    footer?: boolean;
    header?:
      | boolean
      | {
          component?: {
            side: 'left' | 'right';
            el;
          };
        };
    container?: {
      fit?: boolean;
      flex?: boolean;
    };
  };
  children?: Route[];
}

export enum Type {
  DEVICE = 'device',
  HUB = 'hub'
}

export enum FloodSensor {
  BOTTOM_FLOAT_TRIGGER = 'lsi1',
  TOP_FLOAT_TRIGGER = 'lsi2',
  ULRASONIC_SENSOR_TRIGGER = 'water_height_trigger'
}

export interface Prop {
  objectType: Types;
  objectID: string;
  id: string;
  name: string;
  value: any;
  version: string;
  signature: string;
  zorder: number;
  classname: string;
}

export enum Types {
  PROPS = 'properties',
  DEVICES = 'devices',
  COMPANIES = 'companies',
  PROJECTS = 'projects'
}

export interface AlertsMap {
  [key: string]: number;
}

export type TariffDataProp = Record<string, TariffData[]>;

export interface TariffData {
  months: number[];
  peak: number;
  high: number;
  low: number;
  class: string;
}

export type Tariff = Record<string, TariffData[]>;

export type TariffTableRow = TariffData & {
  year: string;
  period: string;
  show_year?: boolean; // Optional, since it’s not always present
};

export type VSelect15Ref = Vue & {
  // common v-input / v-select instance methods in 1.5
  focus: () => void;
  blur: () => void;
  reset: () => void;
  resetValidation: () => void;
  validate: () => boolean;

  // value proxy (v-model)
  internalValue: any;  // current value
};

export type VTextFieldRef = Vue & {
  focus: () => void;
  blur: () => void;
  reset: () => void;
  resetValidation: () => void;
  validate: () => boolean;
  internalValue: any; // actual v-model value
};

export type VFormRef = Vue & {
  validate: () => boolean;
  resetValidation: () => void;
};

export type GmapMapRef = Vue & {
  $mapPromise: Promise<google.maps.Map>;
  $mapObject: google.maps.Map; // the native Google Map
};

// ============================================
// Device Types and Interfaces
// ============================================

// Location interfaces
export interface GeoLocation {
  lat: string | number;
  lng: string | number;
}

export interface InstallLocation {
  gps_location: GeoLocation;
  app_location: GeoLocation;
  gis_location: GeoLocation;
  manual_location: GeoLocation;
}

// Commission interface
export interface MetaCommission {
  date: string;
  commissioner: string;
  commissioned: boolean;
}

// Category interface
export interface MetaCategory {
  category: string;
}

// Hardware ID interface
export interface DeviceHardwareId {
  id?: string;
  mac?: string;
  imei?: string;
}

// Base meta.device fields (common to all devices)
export interface MetaDeviceBase {
  address?: string;
  estimated_address?: string;
  notes?: string;
  description?: string;
  [key: string]: any; // Allow additional dynamic fields
}

// Fixture-specific meta.device fields
export interface MetaDeviceFixture extends MetaDeviceBase {
  cabinet_id?: string;
  circuit_number?: string;
  pole_number?: string;
  phase?: string;
  watts?: string | number;
  fixture_icon?: string;
  fixture_manufacturer?: string;
  pole_height?: string;
  pole_type?: string;
  is_pole_painted?: string;
  screw_type?: string;
  road_curbs?: string;
  scan_numbering?: string;
  total_scan_number?: string;
}

// Cabinet-specific meta.device fields
export interface MetaDeviceCabinet extends MetaDeviceBase {
  cabinet_id?: string;
  cabinet_address?: string;
  managed_area?: string;
  road?: string;
  assigned_devices?: number;
  controlled_devices?: number;
  "iec's_supply_pole_id"?: string;
  "cabinet_iec's_meter_id"?: string;
  "iec's_customer_id"?: string;
}

// Motion Sensor meta.device fields
export interface MetaDeviceSensor extends MetaDeviceBase {
  model?: string;
  manufacturer?: string;
  interface?: string;
}

// Flood Sensor meta.device fields
export interface MetaDeviceFloodSensor extends MetaDeviceBase {
  top_float_height?: string;
  bottom_float_height?: string;
  threshold?: string;
  sensor_height?: string;
}

// Schedule interfaces
export interface DeviceSchedule {
  schedule?: string[];
  scheduler_type?: string;
}

export interface CabinetSchedule {
  schedules?: Record<string, any>;
  days_schedules?: Record<string, any>;
}

export interface ScheduleCurve {
  curves?: Record<string, any>;
  open_schedule?: Record<string, any>;
}

// Dashboard connection display
export interface DashboardConnectionDisplay {
  enable_outputs?: number;
  enable_inputs?: number;
  display_outputs?: number;
  display_inputs?: number;
}

// Services config
export interface ServicesConfig {
  alerts?: Record<string, any>;
  schedule_validation?: Record<string, any>;
  [key: string]: any;
}

// Cabinet devices schedule
export interface CabinetDevicesSchedule {
  active?: boolean;
  is_astronomical?: boolean;
  [key: string]: any;
}

// ============================================
// Main Device Interface
// ============================================

export interface BaseDevice {
  id: string;
  name: string;
  class_name: string;
  project_id: string;
  power?: number;
  dmodified_ts?: number;
  smodified_ts?: number;
  created_ts?: number;
  sys___active?: number | null;
  sys___active__modified?: number;
  sys___connected?: number | null;
  sys___online?: number | null;
  rssi?: number;
  fault?: number;
  disconnect_reason?: string;
  has_valid_location?: boolean;
  has_default_location?: boolean;
  container_id?: string;
  monogoto_status?: string;
  
  // Dot notation meta fields
  'meta.device': MetaDeviceBase;
  'meta.commission': MetaCommission;
  'meta.location': GeoLocation;
  'meta.category': MetaCategory;
  'meta.imsi'?: { imsi: string };
  'meta.type'?: { type: string };
  'meta.part_number'?: { part_number: string };
  'meta.install_location'?: InstallLocation;
  'meta.schedule_curve'?: ScheduleCurve;
  'meta.hardware_ids'?: { eth0: string };
  'meta.devices_schedule'?: CabinetDevicesSchedule;
  'meta.alert_config'?: Record<string, any>;

  // Dot notation device fields
  'device.hardwareid': DeviceHardwareId;
  'device.schedule': DeviceSchedule | CabinetSchedule | {};
  'device.version'?: Record<string, any>;

  // Dot notation optional structures
  'services.config'?: ServicesConfig;
  'dashboard.connection_display'?: DashboardConnectionDisplay;
}

// Fixture device type
export interface FixtureDevice extends Omit<BaseDevice, 'meta.device'> {
  parent_id?: string;
  power_consumption?: number;
  low_power_alert?: number;
  version_nrf52840?: string;
  'meta.device': MetaDeviceFixture;
}

// Cabinet device type
export interface CabinetDevice extends Omit<BaseDevice, 'meta.device' | 'device.schedule'> {
  circuits?: Record<string, any>;
  device_connected_percent?: number;
  'meta.device': MetaDeviceCabinet;
  'device.schedule': CabinetSchedule;
}

// Motion Sensor device type
export interface MotionSensorDevice extends Omit<BaseDevice, 'meta.device'> {
  'meta.device': MetaDeviceSensor;
}

// Flood Sensor device type
export interface FloodSensorDevice extends Omit<BaseDevice, 'meta.device'> {
  water_mark?: number;
  water_height_trigger?: number;
  lsi1?: number;
  lsi2?: number;
  'meta.device': MetaDeviceFloodSensor;
}

// Union type for any device
export type Device = FixtureDevice | CabinetDevice | MotionSensorDevice | FloodSensorDevice | BaseDevice;

// ============================================
// Event and Incident Types
// ============================================

export interface MultilingualString {
  en: string;
  he: string;
  ar: string;
}

export interface EventType {
  event_type: string;
  name: MultilingualString;
  description: MultilingualString;
  severity: number;
  priority: number;
  scope: string;
  created: string;
}

export interface IncidentType {
  incident_type: string;
  name: MultilingualString;
  description: MultilingualString;
  severity: number;
  priority: number;
  scope: string;
  created: string;
}
